/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { computed, Signal } from '@angular/core';
import { signalStore, withState, withMethods, withComputed, patchState, getState } from '@ngrx/signals';
import { constants } from '@well/infra/constants';

export interface WellSystemSettingsState {
  numberOfDaysForCalendarOffset: number;
  currencyList: { label: string; value: string }[];
  dynamicSettings: Record<string, unknown>;
}

const initialState: WellSystemSettingsState = {
  numberOfDaysForCalendarOffset: constants.numberOfDaysForCalendarOffset,
  currencyList: constants.currencyList,
  dynamicSettings: {},
};

export const WellSystemSettingsStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store) => ({
    getSetting<T>(key: string): T | undefined {
      const state = getState(store);
      if (key in state) {
        return (state as Record<string, unknown>)[key] as T;
      }
      return state.dynamicSettings[key] as T | undefined;
    },

    getSettingSignal<T>(key: string): Signal<T | undefined> {
      return computed(() => {
        const state = getState(store);
        if (key in state) {
          return (state as Record<string, unknown>)[key] as T;
        }
        return state.dynamicSettings[key] as T | undefined;
      });
    },

    updateSetting(key: string, value: unknown): void {
      if (key === 'numberOfDaysForCalendarOffset') {
        patchState(store, { numberOfDaysForCalendarOffset: value as number });
      } else if (key === 'currencyList') {
        patchState(store, { currencyList: value as { label: string; value: string }[] });
      } else {
        patchState(store, {
          dynamicSettings: { ...store.dynamicSettings(), [key]: value }
        });
      }
    },

    updateNumberOfDaysForCalendarOffset(value: number): void {
      patchState(store, { numberOfDaysForCalendarOffset: value });
    },
  }))
);
